// src/pages/AchievementsManage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Button, Card, CardContent,
    CardMedia, Alert, CircularProgress, Grid, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import { Add, ArrowBack, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';

const AchievementsManage = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAchievement, setEditingAchievement] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [isAuthenticated, user, authLoading, navigate]);

    useEffect(() => {
        fetchAchievements();
    }, []);

    const fetchAchievements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/achievements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAchievements(res.data);
        } catch (err) {
            console.error(err);
            setError('Грешка при вчитување на достигнувања');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (achievement = null) => {
        if (achievement) {
            setEditingAchievement(achievement);
            setFormData({
                title: achievement.title,
                description: achievement.description,
                image: null
            });
            if (achievement.imageUrl) {
                setImagePreview(achievement.imageUrl); // ✅ R2 URL direktno
            }
        } else {
            setEditingAchievement(null);
            setFormData({ title: '', description: '', image: null });
            setImagePreview(null);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingAchievement(null);
        setFormData({ title: '', description: '', image: null });
        setImagePreview(null);
    };

    const handleChange = (field) => (e) => {
        if (field === 'image') {
            const file = e.target.files[0];
            setFormData({ ...formData, image: file });
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        } else {
            setFormData({ ...formData, [field]: e.target.value });
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const data = new FormData();
            data.append('title', formData.title);
            data.append('description', formData.description);
            if (formData.image) {
                data.append('image', formData.image);
            }

            if (editingAchievement) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/admin/achievements/${editingAchievement.id}`,
                    data,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                setSuccess('Достигнувањето е успешно ажурирано!');
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/admin/achievements`,
                    data,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                setSuccess('Достигнувањето е успешно додадено!');
            }

            handleCloseDialog();
            fetchAchievements();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error saving achievement:', err.response?.data || err.message);
            setError('Грешка при зачувување: ' + (err.response?.data?.message || err.message));
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Дали сте сигурни дека сакате да го избришете ова достигнување?')) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/admin/achievements/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('Достигнувањето е успешно избришано!');
            fetchAchievements();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Грешка при бришење');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Container maxWidth="lg">
                <AdminHeader />
                <Box sx={{ mb: 4 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/sections')} sx={{ mb: 3 }}>
                        Назад кон секции
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                            Уреди "Достигнувања"
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                            Додај ново
                        </Button>
                    </Box>
                </Box>

                {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {loading && achievements.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress />
                    </Box>
                ) : achievements.length === 0 ? (
                    <Typography variant="h6" textAlign="center" color="text.secondary" py={4}>
                        Нема достигнувања. Додајте ново за да започнете.
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {achievements.map(a => (
                            <Grid item xs={12} sm={6} md={4} key={a.id}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {a.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={a.imageUrl} // ✅ R2 URL direktno
                                            alt={a.title}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    )}
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            {a.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {a.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                                            <IconButton color="primary" onClick={() => handleOpenDialog(a)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(a.id)}>
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingAchievement ? 'Уреди достигнување' : 'Додај ново достигнување'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Наслов"
                            value={formData.title}
                            onChange={handleChange('title')}
                            placeholder="Наслов на достигнувањето"
                            required
                            fullWidth
                        />
                        <TextField
                            label="Опис"
                            multiline
                            rows={4}
                            value={formData.description}
                            onChange={handleChange('description')}
                            placeholder="Опис на достигнувањето..."
                            required
                            fullWidth
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {editingAchievement ? 'Промени слика (опционално):' : 'Слика:'}
                            </Typography>
                            <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
                                Избери слика
                                <input type="file" accept="image/*" onChange={handleChange('image')} hidden />
                            </Button>
                            {imagePreview && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{
                                            maxWidth: '100%', maxHeight: '300px',
                                            objectFit: 'contain', borderRadius: '8px',
                                            border: '1px solid #ddd'
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={loading}>Откажи</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading || !formData.title || !formData.description}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Зачувај'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AchievementsManage;